import React, { useRef, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@vibe/core';
import { TourStep, TourPlacement, TourTooltipSize } from '@/config/tours/types';
import TourProgress from './TourProgress';
import { ElementRect } from './hooks/useElementPosition';

interface TourTooltipProps {
  /** Current step configuration */
  step: TourStep | null;
  /** Target element for positioning */
  targetElement: HTMLElement | null;
  /** Target element rect (for positioning) */
  targetRect?: ElementRect;
  /** Whether tooltip is visible */
  isVisible: boolean;
  /** Progress info */
  progress: { current: number; total: number };
  /** Show progress indicator */
  showProgress?: boolean;
  /** Allow skipping */
  allowSkip?: boolean;
  /** Is this the first step */
  isFirstStep: boolean;
  /** Is this the last step */
  isLastStep: boolean;
  /** Callbacks */
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  /** Z-index */
  zIndex?: number;
}

/** Size presets for tooltip width */
const TOOLTIP_SIZES: Record<TourTooltipSize, number> = {
  sm: 300,
  md: 386,
  lg: 464,
  xl: 540,
};

const DEFAULT_TOOLTIP_SIZE: TourTooltipSize = 'md';
const TOOLTIP_OFFSET = 20;
const ARROW_SIZE = 12;

/**
 * Calculate tooltip position based on placement and target rect
 */
function calculatePosition(
  placement: TourPlacement,
  targetRect: ElementRect | undefined,
  tooltipHeight: number,
  tooltipWidth: number
): { top: number; left: number; arrowPosition: 'top' | 'bottom' | 'left' | 'right' } {
  if (!targetRect || targetRect.width === 0) {
    // Center in viewport
    return {
      top: window.innerHeight / 2 - tooltipHeight / 2,
      left: window.innerWidth / 2 - tooltipWidth / 2,
      arrowPosition: 'top',
    };
  }

  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;

  switch (placement) {
    case 'left':
      return {
        top: targetCenterY - tooltipHeight / 2,
        left: targetRect.left - tooltipWidth - TOOLTIP_OFFSET,
        arrowPosition: 'right',
      };
    case 'right':
      return {
        top: targetCenterY - tooltipHeight / 2,
        left: targetRect.right + TOOLTIP_OFFSET,
        arrowPosition: 'left',
      };
    case 'top':
      return {
        top: targetRect.top - tooltipHeight - TOOLTIP_OFFSET,
        left: targetCenterX - tooltipWidth / 2,
        arrowPosition: 'bottom',
      };
    case 'bottom':
      return {
        top: targetRect.bottom + TOOLTIP_OFFSET,
        left: targetCenterX - tooltipWidth / 2,
        arrowPosition: 'top',
      };
    case 'center':
    default:
      return {
        top: window.innerHeight / 2 - tooltipHeight / 2,
        left: window.innerWidth / 2 - tooltipWidth / 2,
        arrowPosition: 'top',
      };
  }
}

/**
 * Clamp position to keep tooltip within viewport
 */
function clampPosition(
  top: number,
  left: number,
  tooltipHeight: number,
  tooltipWidth: number
): { top: number; left: number } {
  const padding = 16;
  return {
    top: Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding)),
    left: Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding)),
  };
}

/**
 * Tooltip component for tour steps
 * Uses manual positioning for reliability
 */
export const TourTooltip: React.FC<TourTooltipProps> = ({
  step,
  targetElement,
  targetRect,
  isVisible,
  progress,
  showProgress = true,
  allowSkip = true,
  isFirstStep,
  isLastStep,
  onNext,
  onPrev,
  onSkip,
  zIndex = 9999,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipHeight, setTooltipHeight] = useState(200);
  const isCentered = step?.placement === 'center' || !targetElement;

  // Welcome step uses a special layout with custom content
  const isWelcomeStep = isCentered && typeof step?.content !== 'string';

  // Get tooltip width from size preset
  const tooltipWidth = TOOLTIP_SIZES[step?.size ?? DEFAULT_TOOLTIP_SIZE];

  // Update tooltip height after render
  useEffect(() => {
    if (tooltipRef.current) {
      setTooltipHeight(tooltipRef.current.offsetHeight);
    }
  }, [step, isVisible]);

  // Calculate position
  const position = useMemo(() => {
    if (isCentered) {
      return {
        top: window.innerHeight / 2 - tooltipHeight / 2,
        left: window.innerWidth / 2 - tooltipWidth / 2,
        arrowPosition: 'top' as const,
      };
    }
    const rawPosition = calculatePosition(
      step?.placement || 'bottom',
      targetRect,
      tooltipHeight,
      tooltipWidth
    );
    const clamped = clampPosition(rawPosition.top, rawPosition.left, tooltipHeight, tooltipWidth);
    return {
      ...clamped,
      arrowPosition: rawPosition.arrowPosition,
    };
  }, [step?.placement, targetRect, tooltipHeight, tooltipWidth, isCentered]);

  // Focus management - focus primary action button (Next/Finish)
  useEffect(() => {
    if (!isVisible || !tooltipRef.current) return;

    // Focus the primary action button (Next) instead of the first focusable element
    const primaryButton = tooltipRef.current.querySelector<HTMLElement>(
      '[data-tour-primary]'
    );
    if (primaryButton) {
      primaryButton.focus();
    }

    const focusableElements = tooltipRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isVisible]);

  if (!step) return null;

  const nextButtonText = step.nextButtonText || (isLastStep ? 'Finish' : 'Next');
  const prevButtonText = step.prevButtonText || 'Back';

  // Arrow styles based on position - speech bubble style pointer
  const arrowStyles: Record<string, React.CSSProperties> = {
    top: {
      top: -ARROW_SIZE,
      left: '50%',
      transform: 'translateX(-50%)',
      borderLeft: `${ARROW_SIZE}px solid transparent`,
      borderRight: `${ARROW_SIZE}px solid transparent`,
      borderBottom: `${ARROW_SIZE}px solid white`,
      filter: 'drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.08))',
    },
    bottom: {
      bottom: -ARROW_SIZE,
      left: '50%',
      transform: 'translateX(-50%)',
      borderLeft: `${ARROW_SIZE}px solid transparent`,
      borderRight: `${ARROW_SIZE}px solid transparent`,
      borderTop: `${ARROW_SIZE}px solid white`,
      filter: 'drop-shadow(0 2px 2px rgba(0, 0, 0, 0.08))',
    },
    left: {
      top: '50%',
      left: -ARROW_SIZE,
      transform: 'translateY(-50%)',
      borderTop: `${ARROW_SIZE}px solid transparent`,
      borderBottom: `${ARROW_SIZE}px solid transparent`,
      borderRight: `${ARROW_SIZE}px solid white`,
      filter: 'drop-shadow(-2px 0 2px rgba(0, 0, 0, 0.08))',
    },
    right: {
      top: '50%',
      right: -ARROW_SIZE,
      transform: 'translateY(-50%)',
      borderTop: `${ARROW_SIZE}px solid transparent`,
      borderBottom: `${ARROW_SIZE}px solid transparent`,
      borderLeft: `${ARROW_SIZE}px solid white`,
      filter: 'drop-shadow(2px 0 2px rgba(0, 0, 0, 0.08))',
    },
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Centered modal backdrop */}
          {isCentered && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
                zIndex: zIndex - 1,
              }}
            />
          )}

          <motion.div
            ref={tooltipRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="tour-tooltip-title"
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: tooltipWidth,
              zIndex,
              backgroundColor: 'white',
              borderRadius: 8,
              boxShadow: isWelcomeStep
                ? '0 15px 50px rgba(0, 0, 0, 0.3)'
                : '0 6px 20px rgba(0, 0, 0, 0.2)',
            }}
          >
            {/* Arrow */}
            {!isCentered && targetElement && (
              <div
                style={{
                  position: 'absolute',
                  width: 0,
                  height: 0,
                  ...arrowStyles[position.arrowPosition],
                }}
              />
            )}

            {isWelcomeStep && !step.showFullNavigation ? (
              /* Welcome step layout — single full-width button, no dots or back/skip */
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  padding: '20px',
                }}
              >
                {/* Custom content (includes icon, title, description) */}
                <div>{step.content}</div>

                {/* Full-width button */}
                {!step.hideNextButton && (
                  <Button
                    onClick={onNext}
                    data-tour-primary
                    kind={Button.kinds.PRIMARY}
                    size={Button.sizes.LARGE}
                    className="tw-w-full"
                  >
                    {nextButtonText}
                  </Button>
                )}
              </div>
            ) : isWelcomeStep && step.showFullNavigation ? (
              /* Welcome step with full navigation — dots + back/next/skip */
              (() => {
                const hasSkip = allowSkip && !step.hideSkipButton;
                const hasBack = !isFirstStep && !step.hideBackButton;
                // When nothing is on the left, center the primary button full-width
                const centerNext = !hasSkip && !hasBack;
                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '20px',
                      padding: '24px',
                    }}
                  >
                    {/* Custom content (includes icon, title, description) */}
                    <div>{step.content}</div>

                    {/* Progress dots */}
                    {showProgress && progress.total > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <TourProgress
                          current={progress.current}
                          total={progress.total}
                          variant="dots"
                          size="md"
                        />
                      </div>
                    )}

                    {/* Footer — centered full-width when no skip/back, otherwise space-between */}
                    {centerNext ? (
                      !step.hideNextButton && (
                        <Button
                          onClick={onNext}
                          data-tour-primary
                          kind={Button.kinds.PRIMARY}
                          size={Button.sizes.LARGE}
                          className="tw-w-full"
                        >
                          {nextButtonText}
                        </Button>
                      )
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                        }}
                      >
                        <div>
                          {hasSkip && (
                            <Button
                              onClick={onSkip}
                              kind={Button.kinds.TERTIARY}
                              size={Button.sizes.MEDIUM}
                              aria-label="Skip tour"
                            >
                              Skip Tour
                            </Button>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {hasBack && (
                            <Button
                              onClick={onPrev}
                              kind={Button.kinds.SECONDARY}
                              size={Button.sizes.MEDIUM}
                            >
                              {prevButtonText}
                            </Button>
                          )}
                          {!step.hideNextButton && (
                            <Button
                              onClick={onNext}
                              data-tour-primary
                              kind={Button.kinds.PRIMARY}
                              size={Button.sizes.MEDIUM}
                            >
                              {nextButtonText}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              /* Standard tooltip layout - matches Figma design */
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  padding: '26px',
                }}
              >
                {/* Content */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <h3
                    id="tour-tooltip-title"
                    style={{
                      fontFamily: "'Figtree', sans-serif",
                      fontSize: 16,
                      fontWeight: 600,
                      color: '#323338',
                      margin: 0,
                      lineHeight: 1.2,
                      letterSpacing: '-0.1px',
                    }}
                  >
                    {step.title}
                  </h3>
                  <div
                    style={{
                      fontFamily: "'Figtree', sans-serif",
                      color: '#676879',
                      fontSize: 14,
                      lineHeight: 1.5,
                    }}
                  >
                    {typeof step.content === 'string' ? (
                      <p style={{ margin: 0 }}>{step.content}</p>
                    ) : (
                      step.content
                    )}
                  </div>
                </div>

                {/* Progress dots */}
                {showProgress && progress.total > 1 && (
                  <div style={{ marginBottom: '16px' }}>
                    <TourProgress
                      current={progress.current}
                      total={progress.total}
                      variant="dots"
                      size="md"
                    />
                  </div>
                )}

                {/* Footer with navigation */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                  }}
                >
                  {/* Skip Tour button (left side) */}
                  <div>
                    {allowSkip && !step.hideSkipButton && (
                      <Button
                        onClick={onSkip}
                        kind={Button.kinds.TERTIARY}
                        size={Button.sizes.MEDIUM}
                        aria-label="Skip tour"
                      >
                        Skip Tour
                      </Button>
                    )}
                  </div>

                  {/* Back and Next buttons (right side) */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {!isFirstStep && !step.hideBackButton && (
                      <Button
                        onClick={onPrev}
                        kind={Button.kinds.SECONDARY}
                        size={Button.sizes.MEDIUM}
                      >
                        {prevButtonText}
                      </Button>
                    )}
                    {!step.hideNextButton && (
                      <Button
                        onClick={onNext}
                        data-tour-primary
                        kind={Button.kinds.PRIMARY}
                        size={Button.sizes.MEDIUM}
                      >
                        {nextButtonText}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TourTooltip;
